import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { FaDownload } from "react-icons/fa";

const ManageOrganisations = () => {
  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [org, setOrg] = useState([]);
  const [pageCount, setPageCount] = useState(1);
  const [loading, setLoading] = useState(false);
  const [downloadingOrgId, setDownloadingOrgId] = useState(null);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const [filterInput, setFilterInput] = useState("");
  const jwtToken = localStorage.getItem("jwt_token");
  const history = useHistory();

  const downloadCsv = (rows, filename) => {
    if (!rows.length) {
      return false;
    }

    const headers = Object.keys(rows[0]);
    const lines = [
      headers.join(","),
      ...rows.map((row) =>
        headers
          .map((header) => {
            const value = row[header] ?? "";
            const escapedValue = String(value).replace(/"/g, '""');
            return escapedValue.includes(",") || escapedValue.includes("\n")
              ? `"${escapedValue}"`
              : escapedValue;
          })
          .join(","),
      ),
    ];

    const blob = new Blob([lines.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);

    return true;
  };

  const isPrimitiveLike = (value) =>
    value == null || ["string", "number", "boolean"].includes(typeof value);

  const isRiskRowObject = (value) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return false;
    }

    const values = Object.values(value);

    return (
      values.length > 0 &&
      values.some((entry) => isPrimitiveLike(entry)) &&
      values.every((entry) => isPrimitiveLike(entry))
    );
  };

  const looksLikeActionCardKey = (value) =>
    /^\d+([-/]\d+)?$/.test(String(value || "").trim());

  const isTruthyFlag = (value) => {
    if (value === true) return true;
    if (value === false || value === null || value === undefined) return false;

    const normalized = String(value).trim().toLowerCase();
    return normalized === "1" || normalized === "true";
  };

  const getPurposeLabel = (record) => {
    const isDefaultRecord =
      Number(record?.training_purpose ?? 0) === 1 &&
      Number(record?.has_read ?? 0) === 0;
    const isTrainingRecord = Number(record?.training_purpose ?? 0) === 0;
    const isReadRecord =
      Number(record?.training_purpose ?? 0) === 1 &&
      Number(record?.has_read ?? 0) === 1;

    if (isDefaultRecord) {
      const categories = [];

      if (isTruthyFlag(record?.has_prevention)) categories.push("Prevention");
      if (isTruthyFlag(record?.has_never_miss)) categories.push("Near miss");
      if (isTruthyFlag(record?.has_doubt_about_reporting)) {
        categories.push("Doubt about reporting");
      }

      if (categories.length) return categories.join(", ");
      return "Incident (actual)";
    }

    if (isTrainingRecord) return "Training";
    if (isReadRecord) return "Read";
    return "";
  };

  const addPurposeColumn = (rows) =>
    rows.map((row) => ({
      ...row,
      purpose: getPurposeLabel(row),
    }));

  const normalizeRiskRows = (source, context = {}) => {
    if (Array.isArray(source)) {
      return source.flatMap((entry) => normalizeRiskRows(entry, context));
    }

    if (!source || typeof source !== "object") {
      return [];
    }

    const groupedItems = Array.isArray(source?.items)
      ? source.items
      : Array.isArray(source?.item)
      ? source.item
      : null;

    if (groupedItems) {
      return normalizeRiskRows(groupedItems, context);
    }

    if (isRiskRowObject(source)) {
      return [
        {
          ...source,
          ...(context.action_card_number && !source.action_card_number
            ? { action_card_number: context.action_card_number }
            : {}),
        },
      ];
    }

    return Object.entries(source).flatMap(([key, value]) => {
      const nextContext = looksLikeActionCardKey(key)
        ? {
            ...context,
            action_card_number: context.action_card_number || key,
          }
        : context;

      return normalizeRiskRows(value, nextContext);
    });
  };

  const createSafeFilename = (value, fallback) => {
    const safeValue = String(value || "")
      .trim()
      .replace(/[^a-z0-9]+/gi, "_")
      .replace(/^_+|_+$/g, "")
      .toLowerCase();

    return safeValue || fallback;
  };

  const fetchRiskRows = async (path) => {
    const response = await fetch(`${process.env.REACT_APP_API_URL}${path}`, {
      method: "get",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwtToken}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data?.message || data?.error || "Unable to fetch risk data");
    }

    return normalizeRiskRows(data?.data || data || []);
  };

  const handleRiskDataDownload = async (organisationId, organisationName) => {
    setDownloadingOrgId(organisationId);

    try {
      const riskData = await fetchRiskRows(`/risk-analysis/${organisationId}`);

      if (!Array.isArray(riskData) || riskData.length === 0) {
        toast.error("No risk data found for this organisation");
        return;
      }

      const safeOrgName = createSafeFilename(
        organisationName,
        `organisation_${organisationId}`,
      );

      const isDownloaded = downloadCsv(
        riskData,
        `${safeOrgName}_risk_analysis.csv`,
      );

      if (isDownloaded) {
        toast.success("Risk data downloaded successfully");
      }
    } catch (error) {
      toast.error(
        error.message || "Something went wrong while downloading risk data",
      );
    } finally {
      setDownloadingOrgId(null);
    }
  };

  const handleAllRiskDataDownload = async () => {
    setIsDownloadingAll(true);

    try {
      const riskData = await fetchRiskRows("/risk-analysis/all");

      if (!Array.isArray(riskData) || riskData.length === 0) {
        toast.error("No risk data found for organisations");
        return;
      }

      const isDownloaded = downloadCsv(
        addPurposeColumn(riskData),
        "all_organisations_risk_analysis.csv",
      );

      if (isDownloaded) {
        toast.success("All organisations risk data downloaded successfully");
      }
    } catch (error) {
      toast.error(
        error.message ||
          "Something went wrong while downloading all organisations risk data",
      );
    } finally {
      setIsDownloadingAll(false);
    }
  };

  const manageOrgApi = (page) => {
    let url = `${process.env.REACT_APP_API_URL}/organisations?page=${page}&limit=${itemsPerPage}&search=${filterInput}`;
    fetch(url, {
      method: "get",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwtToken}`,
      },
    })
      .then((res) => {
        return res.json(page);
      })
      .then((data) => {
        const codeMessage = data.data;
        if (codeMessage) {
          setOrg(codeMessage.organizations);
          setPageCount(Math.ceil(codeMessage.count / itemsPerPage));

          setLoading(false);
        } else {
          response.statusText == 404;
          // toast.error("Something went wrong, Please try again later");
          setLoading(false);
        }
      });
  };
  const handlePageClick = (event) => {
    setCurrentPage(event.selected + 1);
    manageOrgApi(event.selected + 1);
  };

  useEffect(() => {
    const getData = setTimeout(() => {
      manageOrgApi(currentPage);
    }, 500);
    return () => clearTimeout(getData);
  }, [filterInput]);

  const handleUnitNo = (data, index, showMore) => {
    const isShowMore = showMore | false;
    if (data.length > 3) {
      return (
        <div>
          {isShowMore == false ? (
            <div>
              <h5>{data[0].actioncard_set_number}</h5>
              <h5>{data[1].actioncard_set_number}</h5>
              <h5>{data[2].actioncard_set_number}</h5>
            </div>
          ) : (
            data.map((one) => {
              return <h5>{one.actioncard_set_number}</h5>;
            })
          )}

          <button
            className="btn bg_color responsive-btn"
            onClick={(e) => {
              const temp = [...org];
              temp[index].isShowMore = isShowMore == false ? true : false;
              setOrg(temp);
            }}
          >
            {isShowMore == false ? "Show More" : "Show Less"}
          </button>
        </div>
      );
    } else {
      return data.map((one) => {
        return <h5>{one.actioncard_set_number}</h5>;
      });
    }
  };
  return (
    <>
      {loading ? (
        <div className="text-center" style={{ width: "100%" }}>
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      ) : (
        <>
          <div className="row my-2">
            <div className="col">Manage organisations</div>
            <div className="col">
              <span className="mr-2">Search and filters:</span>
              <input
                type="text"
                placeholder="E.g. Filter for “Name, Email & Customer ID"
                value={filterInput}
                onChange={(e) => {
                  setFilterInput(e.target.value);
                }}
              />
            </div>
            <div className="col d-flex justify-content-end align-items-center flex-wrap">
              <button
                className="btn responsive-btn bg_color d-inline-flex align-items-center mr-3 fs-8 m-1"
                type="button"
                onClick={handleAllRiskDataDownload}
                disabled={isDownloadingAll}
                style={{
                  gap: "0.55rem",
                  borderRadius: "8px",
                  padding: "0.7rem 1.2rem",
                  fontWeight: 600,
                  boxShadow: "0 12px 28px rgba(246, 97, 95, 0.22)",
                }}
              >
                {isDownloadingAll ? (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                ) : (
                  <FaDownload size={14} />
                )}
                <span>Download All</span>
              </button>
              <Link to="/manage-organisations/add-organisation">
                <button
                  className="btn responsive-btn float-right bg_color fs-8 m-1"
                  type="button"
                  style={{
                     borderRadius: "8px",
                    padding: "0.7rem 1.2rem",
                    fontWeight: 600,
                  }}
                >
                  Add New Organisation
                </button>
              </Link>
            </div>
          </div>
          <table className="table">
            <thead>
              <tr>
                <th scope="col">ID</th>
                <th scope="col">Name of organisation</th>
                <th scope="col">E-mail address</th>
                <th scope="col">Action-cards set numbers</th>
                <th scope="col">Form</th>
                <th scope="col">Customer ID</th>
                <th scope="col">Employee list</th>
                <th scope="col" className="text-center">
                  Units/Divisions
                </th>
                <th scope="col">Remarks</th>
                <th scope="col" className="text-center">
                  Risk Data
                </th>
                <th scope="col">Action</th>
              </tr>
            </thead>
            <tbody>
              {org &&
                org.map((item, i) => {
                  return (
                    <tr key={item.id}>
                      <th scope="row">{item.id}</th>
                      <td>{item.name}</td>
                      <td>{item.email}</td>
                      <td>
                        {handleUnitNo(item.unit_division, i, item.isShowMore)}
                      </td>
                      <td>{item.form}</td>
                      <td>{item.customer_id}</td>
                      <td>{}</td>
                      <td className="text-center">
                        <Link
                          to={`/manage-organisations/actions?id=${item.id}`}
                        >
                          <button className="btn btn-lg bg_color ">
                            <i className="nav-icon fas fa-eye "></i>
                          </button>
                        </Link>
                      </td>
                      <td>{item?.remarks?.substring(0, 20)}</td>
                      <td className="text-center">
                        <button
                          type="button"
                          className="btn btn-lg bg_color"
                          onClick={() =>
                            handleRiskDataDownload(item.id, item.name)
                          }
                          disabled={downloadingOrgId === item.id}
                        >
                          {downloadingOrgId === item.id ? (
                            <span
                              className="spinner-border spinner-border-sm"
                              role="status"
                              aria-hidden="true"
                            ></span>
                          ) : (
                            <i className="nav-icon fas fa-download"></i>
                          )}
                        </button>
                      </td>
                      <td>
                        <i
                          type="button"
                          className="nav-icon fas fa-edit btn btn-lg bg_color"
                          onClick={() => {
                            history.push({
                              pathname: `/manage-organisations/add-organisation`,
                              search: `?id=${item.id}`,
                            });
                          }}
                        ></i>
                      </td>
                    </tr>
                  );
                })}
              <tr hidden={org.length !== 0}>
                <td colSpan="5" className="text-center">
                  No Data Found
                </td>
              </tr>
            </tbody>
          </table>
        </>
      )}
      <ReactPaginate
        nextLabel="Next"
        onPageChange={handlePageClick}
        pageRangeDisplayed={10}
        pageCount={pageCount}
        previousLabel="Previous"
        pageClassName="page-item"
        pageLinkClassName="page-link"
        previousClassName="page-item"
        previousLinkClassName="page-link"
        nextClassName="page-item"
        nextLinkClassName="page-link"
        breakLabel="..."
        breakClassName="page-item"
        breakLinkClassName="page-link"
        containerClassName="pagination"
        activeClassName="active"
        renderOnZeroPageCount={null}
        forcePage={0}
      />
    </>
  );
};
export default ManageOrganisations;
